import React, { useState, useMemo } from "react";
import {
  Autocomplete,
  Box,
  StandardTextFieldProps,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { COUNTRY_CODES, ICountryCode } from "../../constants/countryCodes";
import { CustomTextField } from "./CustomTextField";

interface CountryCodeSelectProps {
  value?: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  disabled?: boolean;
  sx?: SxProps;
  variant?: StandardTextFieldProps["variant"];
}

export const CountryCodeSelect: React.FC<CountryCodeSelectProps> = ({
  value,
  onChange,
  error = false,
  helperText,
  label = "Country Code",
  disabled = false,
  sx,
  variant,
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");

  // Default to India (+91) if no value is provided
  const defaultValue = "+91";
  const effectiveValue = value || defaultValue;

  // Find the selected country based on the current value
  const selectedCountry = useMemo(() => {
    return (
      COUNTRY_CODES.find((country) => country.dialCode === effectiveValue) ||
      undefined
    );
  }, [effectiveValue]);

  // Set default value only on initial mount if no value is provided
  const hasSetInitialValue = React.useRef(false);
  React.useEffect(() => {
    if (!value && !hasSetInitialValue.current) {
      onChange(defaultValue);
      hasSetInitialValue.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount to set initial default

  const handleChange = (_: any, newValue: ICountryCode | null) => {
    if (newValue) {
      onChange(newValue.dialCode);
      setInputValue(newValue.dialCode);
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  return (
    <Box>
      {/* <CustomFormLabel>{label}</CustomFormLabel> */}
      <Autocomplete
        value={selectedCountry}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={COUNTRY_CODES}
        getOptionLabel={(option) =>
          `${option.flag} ${option.dialCode}                                                                                                                  (${option.code})      (${option.name})`
        }
        isOptionEqualToValue={(option, value) =>
          option.dialCode === value?.dialCode
        }
        disabled={disabled}
        disableClearable={true}
        popupIcon={null}
        renderInput={(params) => (
          <CustomTextField
            {...params}
            label={label}
            placeholder="Search by country name, code, or dial code..."
            value={params.inputProps.value}  
            error={error}
            helperText={helperText}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "5px",
                fontSize: "0.87rem",
                "& fieldset": {
                  borderColor: error
                    ? theme.palette.error.main
                    : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.12)"
                    : "#dee3e9",
                },
                "&:hover fieldset": {
                  borderColor: error
                    ? theme.palette.error.main
                    : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.23)"
                    : "#b0b0b0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: error
                    ? theme.palette.error.main
                    : theme.palette.primary.main,
                },
              },
              "& .MuiInputBase-input": {
                padding: "9px 13px",
                fontSize: "0.87rem",
                color: "rgb(38, 38, 38)",
                textOverflow: "clip",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#767e89",
                opacity: "1",
              },
            }}
            variant={variant}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "8px 16px",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography sx={{ fontSize: "1.2rem" }}>{option.flag}</Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, minWidth: "30px" }}
            >
              {option.dialCode}
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              {option.name}
            </Typography> */}
          </Box>
        )}
        renderTags={() => null} // Don't render tags for single selection
        noOptionsText="No countries found"
        clearOnBlur={false}
        selectOnFocus
        handleHomeEndKeys
        sx={{
          ...sx,
          // Outlined variant
          ".MuiOutlinedInput-root": {
            paddingTop: "1px",
            paddingBottom: "1px",
            paddingRight: "9px !important",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
          },
          // Standard variant
          ".MuiInput-root": {
            paddingRight: "0px !important",
            fontSize: "0.8rem",
            color: "rgb(38, 38, 38)",
          },
          "& .MuiAutocomplete-endAdornment": {
            display: "none",
          },
          // Remove ellipsis for both variants
          "& .MuiInputBase-input": {
            textOverflow: "clip !important",
          },
        }}
      />
    </Box>
  );
};
